/**
 * Master scheduler — Chris Wilson "Tale of Two Clocks" pattern.
 *
 * The trick: we run an *imprecise* polling loop on the main thread that
 * looks ahead by ~100 ms, and we use the *precise* AudioContext clock to
 * tag each row's intended fire time. Consumers (the engine) receive a row
 * event with an `audioTime` and schedule audio-thread events (AudioParams,
 * etc.) at exactly that time — sample-accurate, regardless of jitter in
 * the polling loop.
 *
 * The scheduler itself is project-aware enough to know about the order
 * list and pattern row count, but doesn't know what's *in* the patterns.
 * That's the engine's job. This keeps the scheduler testable in pure
 * Node without any AudioContext.
 */

/** A minimal clock interface — mock in tests, pass `audioContext` in production. */
export interface AudioClock {
  /** Current time in seconds. AudioContext.currentTime satisfies this. */
  readonly currentTime: number;
}

export interface RowEvent {
  /** Index into `orderList`. */
  orderIndex: number;
  /** Pattern that the cursor is currently inside (orderList[orderIndex]). */
  patternIndex: number;
  /** Row within the pattern (0..patternRowCount-1). */
  rowInPattern: number;
  /** AudioContext time at which this row should fire. */
  audioTime: number;
}

export interface SchedulerConfig {
  clock: AudioClock;
  /** Duration of one tracker row in seconds. */
  rowDurationSec: number;
  /** Rows per pattern (typically 64). */
  patternRowCount: number;
  /** Pattern indices to play in sequence. */
  orderList: number[];
  /** Order-list index to jump to when the song ends. Set to null for one-shot. */
  loopStartOrderIndex: number | null;
  /** Fired for every row that crosses the look-ahead window. */
  onRow: (event: RowEvent) => void;
  /** Defaults to 0.1 s — how far ahead we schedule events. */
  lookAheadSec?: number;
}

export interface StartPosition {
  /** Order-list index to begin at. Defaults to 0. */
  orderIndex?: number;
  /** Row within that pattern to begin at. Defaults to 0. */
  rowInPattern?: number;
}

export interface Scheduler {
  /** Begin playback. Defaults to (orderIndex=0, rowInPattern=0). */
  start(at?: StartPosition): void;
  /** Halt playback and reset cursor to the start. */
  stop(): void;
  /** Halt without resetting; resume() picks up where it left off. */
  pause(): void;
  /** Resume after pause(). */
  resume(): void;
  isPlaying(): boolean;
  /**
   * Update the row duration. Takes effect on the next row firing — already-
   * scheduled rows keep their original time. Use to live-change tempo/speed.
   */
  setRowDurationSec(seconds: number): void;
  /** Current row duration in seconds. */
  getRowDurationSec(): number;
  /**
   * Internal: process all rows that fall within `[clock.currentTime,
   * clock.currentTime + lookAhead]` and fire onRow for each. Exposed so
   * tests can step the scheduler deterministically using fake clocks.
   * Production code typically only sees this fired by the internal
   * setInterval polling loop.
   */
  tick(): void;
  /** For introspection / UI playhead display. */
  getCurrentRow(): { orderIndex: number; rowInPattern: number } | null;
}

const DEFAULT_LOOK_AHEAD_SEC = 0.1;
const POLL_INTERVAL_MS = 25;

/**
 * Create a scheduler. The polling interval starts on `start()` and stops
 * on `stop()` / `pause()`.
 */
export function createScheduler(config: SchedulerConfig): Scheduler {
  if (config.orderList.length === 0) {
    throw new Error('Scheduler requires a non-empty orderList.');
  }
  if (config.patternRowCount <= 0) {
    throw new Error(`patternRowCount must be > 0 (got ${config.patternRowCount}).`);
  }
  if (config.rowDurationSec <= 0) {
    throw new Error(`rowDurationSec must be > 0 (got ${config.rowDurationSec}).`);
  }

  const lookAhead = config.lookAheadSec ?? DEFAULT_LOOK_AHEAD_SEC;

  let playing = false;
  let pollHandle: ReturnType<typeof setInterval> | null = null;
  let nextRowTime = 0;
  let rowDurationSec = config.rowDurationSec;
  let cursor: { orderIndex: number; rowInPattern: number } = {
    orderIndex: 0,
    rowInPattern: 0,
  };
  let songEnded = false;

  function resetCursor(at?: StartPosition) {
    const o = at?.orderIndex ?? 0;
    const r = at?.rowInPattern ?? 0;
    const clampedO = Math.max(0, Math.min(config.orderList.length - 1, Math.floor(o)));
    const clampedR = Math.max(0, Math.min(config.patternRowCount - 1, Math.floor(r)));
    cursor = { orderIndex: clampedO, rowInPattern: clampedR };
    songEnded = false;
  }

  function advanceCursor(): boolean {
    cursor.rowInPattern++;
    if (cursor.rowInPattern >= config.patternRowCount) {
      cursor.rowInPattern = 0;
      cursor.orderIndex++;
      if (cursor.orderIndex >= config.orderList.length) {
        if (config.loopStartOrderIndex === null) {
          songEnded = true;
          return false;
        }
        cursor.orderIndex = config.loopStartOrderIndex;
      }
    }
    return true;
  }

  function tick(): void {
    if (!playing || songEnded) return;
    const horizon = config.clock.currentTime + lookAhead;
    while (nextRowTime < horizon && !songEnded) {
      const patternIndex = config.orderList[cursor.orderIndex]!;
      config.onRow({
        orderIndex: cursor.orderIndex,
        patternIndex,
        rowInPattern: cursor.rowInPattern,
        audioTime: nextRowTime,
      });
      const advanced = advanceCursor();
      nextRowTime += rowDurationSec;
      if (!advanced) break;
    }
    if (songEnded) {
      playing = false;
      if (pollHandle !== null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    }
  }

  function startPolling(): void {
    if (pollHandle !== null) return;
    pollHandle = setInterval(tick, POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (pollHandle !== null) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
  }

  return {
    start(at?: StartPosition) {
      if (playing) return;
      resetCursor(at);
      playing = true;
      nextRowTime = config.clock.currentTime;
      tick(); // fire any immediately-due rows
      startPolling();
    },

    stop() {
      playing = false;
      stopPolling();
      resetCursor();
    },

    pause() {
      if (!playing) return;
      playing = false;
      stopPolling();
    },

    resume() {
      if (playing || songEnded) return;
      playing = true;
      // nextRowTime stays at its prior value but we re-anchor to "now" so
      // we don't try to fire a backlog of rows that are already in the past.
      const now = config.clock.currentTime;
      if (nextRowTime < now) nextRowTime = now;
      tick();
      startPolling();
    },

    isPlaying: () => playing,

    setRowDurationSec(sec: number) {
      if (sec <= 0) throw new Error(`rowDurationSec must be > 0 (got ${sec}).`);
      rowDurationSec = sec;
    },

    getRowDurationSec: () => rowDurationSec,

    tick,

    getCurrentRow() {
      if (!playing) return null;
      return { orderIndex: cursor.orderIndex, rowInPattern: cursor.rowInPattern };
    },
  };
}
